/**
 * THE WIRE BOUNDARY for MCP connectors — and it is ASYMMETRIC.
 *
 *   REQUESTS  → snake_case.  `tool_prefix`, `is_enabled`, `credential`.
 *   RESPONSES → camelCase, always, nested objects included.
 *
 * That is not guessable, which is exactly why it is written here and pinned by a
 * test on the server side too. The request bodies mirror the columns; the
 * responses are mapped to client shapes before they leave the route.
 *
 * So this module carries a real request mapping and NO response renaming. What
 * the read functions do instead is NARROW — see `toStatus`.
 *
 * ## How this file got written wrong first, which is the useful part
 *
 * Registration was 400ing. Reading the server branch showed four separate
 * mismatches rather than the one reported, and I built a full snake_case ⇄
 * camelCase mapper for all of them.
 *
 * Half of that was wrong. I had read `listServersForUser`'s SQL row type — the
 * `executeRawQuery<{ is_enabled, connected, tool_count }>` generic — and stopped
 * there, never reaching the `.map()` twenty lines below that converts it to
 * camelCase, nests `connection`, and parses `toolCount` before the route ever
 * returns it. The snake_case names were real; they were just the shape coming OUT
 * OF POSTGRES, not the shape going out of the API.
 *
 * A response mapper built on that would have re-mapped already-mapped data:
 * `isEnabled` unread, so every server rendering as "Disabled"; `connection`
 * rebuilt from three undefined fields. It would have reproduced the exact bug it
 * was written to fix.
 *
 * **And the contract test PASSED, because I wrote its fixtures from the same
 * wrong source.** Its header claimed the fixtures were the shapes the API
 * actually sends. They were not. A test built from the same misreading as the
 * code agrees with itself and proves nothing — which is precisely the failure
 * that header warned about, committed one function away from the warning.
 *
 * The rule that would have caught it: **read to the RETURN statement.** A row
 * type is what a query yields, not what a caller receives.
 */
import type { McpServer, McpServerStatus } from "./crmMcpApi";
import type { McpServerResponse } from "../../../shared/mcpConnectorContract";

/**
 * One server row as the API RETURNS it — already camelCase, already nested,
 * `toolCount` already a number.
 *
 * **This used to be a hand-written literal here, and that was the whole bug.**
 * The contract was declared twice, once on each side of a boundary the two trees
 * cannot import across, so a rename on one side became `undefined` on the other
 * rather than a build error. It cost "every healthy server renders Disabled"
 * once and "a failed probe loses its reason" once, on the same day.
 *
 * It is one declaration in `shared/` now, so the compiler compares the ends
 * instead of a reviewer. See that file's header for the asymmetry — requests are
 * snake_case, responses are camelCase — which is not guessable from either side
 * on its own.
 */
export type WireServer = McpServerResponse;

/**
 * The one thing the read side genuinely has to do.
 *
 * The server types `status` as a union but reaches it through a cast on the
 * column (`r.status as McpServerStatus`), so a value the database holds and the
 * union does not will arrive here wearing the right type and the wrong contents.
 * Narrowing turns that into `null` — read everywhere as "not working", the safe
 * direction.
 *
 * Trusting the declared type instead would let a status added later pass every
 * `=== "connected"` comparison as a silent false: a healthy server shown as
 * broken, with nothing in either codebase to point at.
 */
export function toStatus(raw: string | null | undefined): McpServerStatus {
  return raw === "connected" || raw === "error" || raw === "connecting" ? raw : null;
}

/**
 * Read a server row. A narrowing pass, NOT a rename — every field below keeps
 * the name the API gave it.
 */
export function readServer(w: WireServer): McpServer {
  return {
    id: w.id,
    name: w.name,
    url: w.url,
    enabled: w.isEnabled,
    status: toStatus(w.status),
    statusErrorCode: w.statusErrorCode,
    connection: {
      connected: w.connection?.connected === true,
      needsReconnect: w.connection?.needsReconnect === true,
      needsReconnectCode: w.connection?.needsReconnectCode ?? null,
    },
    // Already a number. Guarded only against a non-finite one, which must show
    // NO count rather than a false one: "we do not know" and "we asked, and the
    // server advertises none" are different claims, and rendering the first as
    // the second states something untrue on a management screen.
    toolCount: Number.isFinite(w.toolCount) ? w.toolCount : undefined,
    // Undefined means NOT KNOWN YET, not "bearer". The column exists (V194) but
    // the list endpoint does not select it yet, and quietly defaulting the
    // unknown case to paste is what would keep the OAuth flow invisible.
    authKind: w.authKind,
  };
}

/**
 * The health-only shape. `register` and `test` answer with this rather than a
 * whole row: the caller already has the id and only these fields changed. The
 * list query is invalidated either way, which is what actually refreshes the
 * screen — this is typed so a caller can read the outcome immediately, not so it
 * can rebuild the row itself.
 */
export interface McpServerHealth {
  status: McpServerStatus;
  statusErrorCode?: string | null;
}

export function readHealth(raw: {
  status?: string | null;
  statusErrorCode?: string | null;
}): McpServerHealth {
  return { status: toStatus(raw.status), statusErrorCode: raw.statusErrorCode ?? null };
}

// ---------------------------------------------------------------------------
// REQUESTS — snake_case, and this half of the boundary is real.
// ---------------------------------------------------------------------------

/**
 * Registration body.
 *
 * `tool_prefix` is NOT sent. It namespaces the tool names the model sees, so it
 * has to be unique across the customer's registrations — and uniqueness is only
 * knowable server-side, inside the transaction that inserts the row. It was
 * briefly required of the caller, which made it the one field a client
 * structurally cannot get right: a collision would have failed with an error
 * naming a field the operator never saw and could not have set.
 *
 * `transport` is not sent either, for a different reason — HTTP is the only one
 * that can succeed, so the form states that in a sentence rather than offering a
 * control whose other options are always refused.
 *
 * Fields are listed one by one rather than spread, so a field added to the form
 * has to be named HERE to reach the network. The body schema is `.strict()`, so
 * a stray camelCase key is a 400, not a harmless extra.
 */
export function toWireRegisterBody(input: {
  name: string;
  url: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
}): Record<string, string> {
  return {
    name: input.name,
    url: input.url,
    ...(input.oauthClientId ? { oauth_client_id: input.oauthClientId } : {}),
    ...(input.oauthClientSecret ? { oauth_client_secret: input.oauthClientSecret } : {}),
  };
}

/**
 * Update body. `enabled` for us, `is_enabled` on the wire — the column's name.
 *
 * An absent key means "leave it alone", so `undefined` is dropped rather than
 * sent: `{is_enabled: undefined}` serialises to `{}` here, but would mean
 * something different the day this becomes a PUT.
 */
export function toWireUpdateBody(input: {
  name?: string;
  enabled?: boolean;
}): Record<string, string | boolean> {
  return {
    ...(input.name === undefined ? {} : { name: input.name }),
    ...(input.enabled === undefined ? {} : { is_enabled: input.enabled }),
  };
}

/**
 * Connect body. The field is `credential` on the wire.
 *
 * This is the one rejection on the whole surface that does NOT name its field,
 * on purpose and on both sides: the machinery that names a field also echoes
 * what it received, and what it received here is somebody's token.
 */
export function toWireConnectBody(token: string): { credential: string } {
  return { credential: token };
}
